'use client';

/**
 * Account panel view — the ONE person cluster behind the rail avatar (U2):
 * identity, plan, Notifications first (the rail bell is gone; the unread
 * badge rides the avatar and leads here), the account actions, sign out,
 * and one quiet bottom row: legal links left, the theme segment right.
 * The workspace switcher is NOT duplicated here: that's Stage's corner.
 */

import { useEffect, useState } from 'react';
import {
  Bell,
  Download,
  LifeBuoy,
  LogIn,
  LogOut,
  Monitor,
  Moon,
  Settings,
  Shield,
  Sparkles,
  Sun,
  UserCircle,
} from 'lucide-react';
import { Avatar } from '@/components/avatar';
import { useAuth } from '@/components/auth-provider';
import { useT } from '@/components/i18n-provider';
import { useTheme } from '@/components/theme-provider';
import { useNotifications } from '@/hooks/useNotifications';
import { useFullscreenNav } from '@/components/workspace/fullscreen-nav';
import { openWorkspacePanel } from '@/components/workspace/panels/panel-events';
import { displayPlanName } from '@/lib/plans/display';
import { isGuestUser } from '@/lib/auth/is-guest';
import { cn } from '@/lib/utils';

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-1 pb-1.5 pt-4 text-[11px] font-medium tracking-tight text-muted-foreground/80">
      {children}
    </div>
  );
}

function Row({
  icon,
  label,
  onClick,
  danger,
  trailing,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  danger?: boolean;
  /** Right-aligned adornment (e.g. the unread-count chip). */
  trailing?: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-left text-[13px] transition-colors',
        danger
          ? 'text-red-500 hover:bg-red-500/10'
          : 'text-foreground/90 hover:bg-accent/60 hover:text-foreground',
      )}
    >
      <span className={cn('shrink-0', danger ? 'text-red-500' : 'text-muted-foreground')}>
        {icon}
      </span>
      <span className="flex-1 truncate">{label}</span>
      {trailing}
    </button>
  );
}

const THEMES = [
  { value: 'light', labelKey: 'settings.light', icon: Sun },
  { value: 'dark', labelKey: 'settings.dark', icon: Moon },
  { value: 'system', labelKey: 'settings.system', icon: Monitor },
] as const;

export function AccountView() {
  const t = useT();
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const { openHref } = useFullscreenNav();
  const { unreadCount } = useNotifications(!!user);
  const [planName, setPlanName] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    fetch('/api/balance')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (alive) setPlanName(displayPlanName(d?.planName));
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  if (!user) return null;
  const isGuest = isGuestUser(user);
  // Admits admin and moderator only. `role` may be absent on the session user,
  // in which case this is false and the Admin row stays hidden — fail closed.
  const isStaff = user?.role === 'admin' || user?.role === 'moderator';

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-3 pb-4">
      {/* Identity */}
      <div className="flex items-center gap-3 px-1 pt-1">
        <Avatar
          seed={user.username}
          type={user.accountType === 'agent' ? 'agent' : 'human'}
          src={user.avatarUrl}
          className="h-12 w-12 shrink-0"
        />
        <div className="min-w-0">
          <div className="truncate text-[15px] font-semibold leading-tight">
            {user.displayName || user.username}
          </div>
          <div className="truncate text-[13px] text-muted-foreground">@{user.username}</div>
        </div>
      </div>

      {/* Subscription card */}
      {!isGuest && (
        <div className="mt-4 flex items-center gap-3 rounded-xl bg-foreground/[0.025] p-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-primary">
            <Sparkles className="h-[18px] w-[18px]" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              {t('wallet.planLabel')}
            </div>
            <div className="truncate text-[15px] font-semibold leading-tight">
              {planName || t('wallet.freePlanName')}
            </div>
          </div>
          <button
            type="button"
            onClick={() => openHref('/account')}
            className={cn(
              'shrink-0 rounded-lg px-3.5 py-1.5 text-[13px] font-medium transition-colors',
              planName
                // Existing plan: a quiet, neutral "Manage" that doesn't shout.
                ? 'border border-border/70 bg-background/70 text-foreground hover:bg-accent/70'
                // No plan yet: a prominent upgrade CTA.
                : 'bg-primary text-primary-foreground hover:bg-primary/90',
            )}
          >
            {planName ? t('user.manage') : t('user.upgrade')}
          </button>
        </div>
      )}

      {/* Notifications — first thing in the menu (U2): the rail bell is
          gone, the unread badge rides the avatar, and this row is where the
          badge leads. Opens the existing notifications VIEW of this panel. */}
      <div className="mt-4 flex flex-col rounded-xl bg-foreground/[0.025] p-1">
        <Row
          icon={<Bell className="h-4 w-4" />}
          label={t('nav.notifications')}
          trailing={
            unreadCount > 0 ? (
              <span className="inline-flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-semibold leading-none text-white">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            ) : undefined
          }
          onClick={() => openWorkspacePanel('left', { tab: 'notifications' })}
        />
      </div>

      {/* Menu — grouped on a soft surface so the actions read as one block.
          Appearance collapsed into the bottom preference rows (U2). */}
      <SectionLabel>{t('nav.account')}</SectionLabel>
      <div className="flex flex-col rounded-xl bg-foreground/[0.025] p-1">
        {!isGuest && (
          <Row
            icon={<UserCircle className="h-4 w-4" />}
            label={t('account.manageAccount')}
            onClick={() => openHref('/account')}
          />
        )}
        {/* Admin — staff only. Sits beside Account hub rather than inside it:
            it is a platform surface, not a personal-account one. Hiding the row
            is presentation, NOT protection — AdminView redirects non-staff and
            every /api/admin route re-checks the role server-side — but a
            regular user must never be shown an affordance they cannot use.
            `role` is optional on the session user, so this fails CLOSED. */}
        {!isGuest && isStaff && (
          <Row
            icon={<Shield className="h-4 w-4" />}
            label={user?.role === 'admin' ? t('sidebar.adminLabel') : t('sidebar.moderationLabel')}
            onClick={() => openHref('/admin')}
          />
        )}
        {/* Settings is available to guests too: it now hosts theme, language
            and the rail auto-hide toggle, all of which work without an
            account. The economy / profile tabs inside gate themselves. */}
        <Row
          icon={<Settings className="h-4 w-4" />}
          label={t('user.settings')}
          onClick={() => openHref('/settings')}
        />
        {!isGuest && (
          <Row
            icon={<Download className="h-4 w-4" />}
            label={t('nav.installApp')}
            onClick={() => openHref('/local')}
          />
        )}
        <Row
          icon={<LifeBuoy className="h-4 w-4" />}
          label={t('nav.helpDocs')}
          onClick={() => window.open('/docs', '_blank', 'noopener,noreferrer')}
        />
      </div>

      <div className="mt-2 px-1">
        <Row
          icon={<LogOut className="h-4 w-4" />}
          label={isGuest ? t('auth.endGuestSession') : t('auth.signOut')}
          onClick={() => logout()}
          danger
        />
      </div>

      {/* Guest conversion card, last block in the panel: it is the takeaway,
          not a header, and sitting under the destructive "End guest session"
          gives the constructive option the final word. Signing up upgrades the
          guest IN PLACE (keeps their workspaces) via the /signup dialog. */}
      {isGuest && (
        <div className="mt-4 rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/[0.10] via-primary/[0.04] to-transparent p-4">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-primary">
            {t('account.guestBadge')}
          </div>
          <div className="mt-0.5 text-[15px] font-semibold leading-snug">
            {t('account.guestSaveTitle')}
          </div>
          {/* 14 = guest GC inactivity TTL (GUEST_GC_DEFAULT_INACTIVITY_DAYS). */}
          <p className="mt-1 text-[12px] leading-snug text-muted-foreground">
            {t('account.guestSaveBody')}
          </p>
          <button
            type="button"
            onClick={() => openHref('/signup')}
            className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-3.5 py-2 text-[13px] font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
          >
            <LogIn className="h-4 w-4" />
            {t('account.signUpFree')}
          </button>
        </div>
      )}

      {/* Bottom preference rows (U2): the least-changed controls, smallest
          form. Theme keeps all three states as an icon segment; Hide rail is
          a SHORTCUT to the Settings preference (the Settings copy stays);
          the legal line gives Terms / Privacy a stable in-product address —
          the full documents live in docs. `mt-auto` pins the cluster to the
          panel foot when the menu is short. */}
      <div className="mt-auto flex items-center justify-between gap-3 px-1 pt-6">
        <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground/70">
          <a
            href="/doc/terms-of-service"
            target="_blank"
            rel="noopener noreferrer"
            className="transition-colors hover:text-foreground"
          >
            {t('legal.terms')}
          </a>
          <span aria-hidden>·</span>
          <a
            href="/doc/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
            className="transition-colors hover:text-foreground"
          >
            {t('legal.privacy')}
          </a>
        </div>
        <div className="flex gap-0.5 rounded-lg bg-foreground/[0.05] p-0.5">
            {THEMES.map((o) => {
              const active = theme === o.value;
              return (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => setTheme(o.value)}
                  aria-pressed={active}
                  aria-label={t(o.labelKey)}
                  title={t(o.labelKey)}
                  className={cn(
                    'flex h-6 w-7 items-center justify-center rounded-md transition-all',
                    active
                      ? 'bg-background text-foreground shadow-sm ring-1 ring-border/50'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  <o.icon className="h-3.5 w-3.5" />
                </button>
              );
            })}
        </div>
      </div>
    </div>
  );
}
